import React from 'react';

import { Stack } from '@atlaskit/primitives/compiled';

import { useChildren } from '../../hooks/useChildren.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type {
	A2UIColumnProps,
	JustifyValue,
	AlignValue,
} from '../../types/A2UIComponents.types.tsx';

type StackAlignBlock = 'start' | 'center' | 'end' | 'stretch';
type StackAlignInline = 'start' | 'center' | 'end' | 'stretch';

const mapJustify = (
	justify?: JustifyValue,
): {
	alignBlock?: StackAlignBlock;
	spread?: 'space-between';
	xcssKey?: 'spaceAround' | 'spaceEvenly';
} => {
	switch (justify) {
		case 'start':
		case 'center':
		case 'end':
		case 'stretch':
			return { alignBlock: justify };
		case 'spaceBetween':
			return { spread: 'space-between' };
		case 'spaceAround':
			return { xcssKey: 'spaceAround' };
		case 'spaceEvenly':
			return { xcssKey: 'spaceEvenly' };
		default:
			return {};
	}
};

const mapAlign = (align?: AlignValue): StackAlignInline | undefined => {
	return align;
};

type Props = {
	definition: A2UIComponentDefinition;
	visited?: Set<string>;
};

export const A2UIColumn = ({ definition, visited }: Props): React.JSX.Element => {
	const def = definition as A2UIColumnProps;
	const children = useChildren(def.children, visited);
	const { alignBlock, spread } = mapJustify(def.justify);
	const alignInline = mapAlign(def.align);

	return (
		<Stack space="space.100" alignBlock={alignBlock} alignInline={alignInline} spread={spread}>
			{children}
		</Stack>
	);
};
